import { AnimatePresence } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GeofenceLayer } from '@/components/map/geofence-layer';
import { MapControls } from '@/components/map/map-controls';
import { MapProvider, useMap } from '@/components/map/map-context';
import { RouteLayer } from '@/components/map/route-layer';
import { VehicleLayer } from '@/components/map/vehicle-layer';
import { VehicleDetailPanel } from '@/components/map/vehicle-detail-panel';
import { VehicleHoverCard } from '@/components/map/vehicle-hover-card';
import { FleetComplianceStrip } from '@/components/vehicles/fleet-compliance-strip';
import { FleetStatCards } from '@/components/vehicles/fleet-stat-cards';
import { DRAWER_WIDTH, VehicleDrawer } from '@/components/vehicles/vehicle-drawer';
import { useFleet } from '@/hooks/use-fleet';
import { boundsOf } from '@/lib/geo';
import { useActiveGeofences } from '@/lib/queries/geofences';
import { useTrail } from '@/lib/queries/trips';
import { useUiStore } from '@/stores/ui-store';
import type { FleetSnapshot } from '@/hooks/use-fleet';

export function LiveMapRoute() {
  const fleet = useFleet();

  return (
    <div className="relative h-full w-full overflow-hidden">
      <MapProvider className="absolute inset-0 h-full w-full">
        <MapScene fleet={fleet} />
      </MapProvider>

      {/* Counts float over the map, clear of the controls on the left and the
          panel on the right. */}
      <div className="pointer-events-none absolute top-4 left-4 z-10 flex max-w-[620px] flex-col gap-2.5 pl-14">
        <FleetStatCards counts={fleet.counts} />
        <FleetComplianceStrip immobilised={fleet.immobilised} />
      </div>

      <VehicleDrawer fleet={fleet} />
    </div>
  );
}

/**
 * Everything that needs the map instance. Split from the route so the provider
 * can render its canvas before any layer tries to attach to it.
 */
function MapScene({ fleet }: { fleet: FleetSnapshot }) {
  const { map } = useMap();
  const hoverAnchorRef = useRef<HTMLDivElement>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const selectedVehicleId = useUiStore((s) => s.selectedVehicleId);
  const selectVehicle = useUiStore((s) => s.selectVehicle);
  const flyToVehicleId = useUiStore((s) => s.flyToVehicleId);
  const consumeFlyTo = useUiStore((s) => s.consumeFlyTo);
  const showGeofences = useUiStore((s) => s.showGeofences);
  const showTrails = useUiStore((s) => s.showTrails);
  const drawerOpen = useUiStore((s) => s.drawerOpen);

  const { data: geofences } = useActiveGeofences(showGeofences);
  const selected = selectedVehicleId ? (fleet.byId.get(selectedVehicleId) ?? null) : null;
  const hovered = hoveredId ? (fleet.byId.get(hoveredId) ?? null) : null;

  const trailRange = useMemo(
    () => ({ from: new Date(Date.now() - 60 * 60_000).toISOString(), limit: 500 }),
    // Recomputing this on every render would refetch the trail continuously;
    // an hour-long window only needs to move when the selection does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedVehicleId],
  );
  const { data: trail } = useTrail(selectedVehicleId, trailRange, showTrails && Boolean(selectedVehicleId));

  const trailCoordinates = useMemo<[number, number][]>(
    () => (trail ?? []).map((point) => [point.lng, point.lat] as [number, number]),
    [trail],
  );

  const fitAll = useCallback(() => {
    if (!map) return;
    const points = fleet.visible
      .filter((entry) => entry.lat !== null && entry.lng !== null)
      .map((entry) => ({ lat: entry.lat!, lng: entry.lng! }));

    const bounds = boundsOf(points);
    if (!bounds) return;

    // Leave room for the panel so a fitted fleet is never hidden beneath it.
    map.fitBounds(
      [
        [bounds.west, bounds.south],
        [bounds.east, bounds.north],
      ],
      {
        padding: { top: 72, bottom: 72, left: 72, right: drawerOpen ? 380 : 72 },
        maxZoom: 15,
        duration: 900,
      },
    );
  }, [map, fleet.visible, drawerOpen]);

  /* First load: frame the fleet once positions exist. */
  const framed = useRef(false);
  useEffect(() => {
    if (framed.current || !map || fleet.visible.length === 0) return;
    framed.current = true;
    fitAll();
  }, [map, fleet.visible.length, fitAll]);

  /* A drawer row or search result asked the map to move. */
  useEffect(() => {
    if (!map || !flyToVehicleId) return;
    const entry = fleet.byId.get(flyToVehicleId);
    consumeFlyTo();
    if (!entry || entry.lat === null || entry.lng === null) return;

    map.flyTo({
      center: [entry.lng, entry.lat],
      zoom: Math.max(map.getZoom(), 14.5),
      duration: 900,
      essential: true,
    });
  }, [map, flyToVehicleId, fleet.byId, consumeFlyTo]);

  /* Escape clears the selection, the same as clicking bare map. */
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') selectVehicle(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectVehicle]);

  return (
    <>
      <GeofenceLayer geofences={geofences?.items ?? []} visible={showGeofences} />
      {showTrails && trailCoordinates.length > 1 && (
        <RouteLayer id="live-trail" coordinates={trailCoordinates} width={2.5} opacity={0.75} />
      )}
      <VehicleLayer
        vehicles={fleet.visible}
        selectedId={selectedVehicleId}
        onSelect={(id) => selectVehicle(id)}
        onHover={setHoveredId}
        hoverAnchorRef={hoverAnchorRef}
      />

      <MapControls onFitAll={fitAll} />

      {/* Positioned imperatively by VehicleLayer so it tracks a moving marker;
          pointer-events off so the card can never steal its own hover. */}
      <div
        ref={hoverAnchorRef}
        className="pointer-events-none absolute top-0 left-0 z-30"
        style={{ visibility: 'hidden' }}
      >
        <div className="-translate-x-1/2 -translate-y-[calc(100%+20px)]">
          <AnimatePresence>
            {hovered && hovered.vehicle.id !== selectedVehicleId && (
              <VehicleHoverCard key={hovered.vehicle.id} entry={hovered} />
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Sits below the map, clear of the fleet panel, so it never covers the
          marker it is describing. */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-20"
        style={{ right: drawerOpen ? DRAWER_WIDTH : 0 }}
      >
        <AnimatePresence>
          {selected && (
            <VehicleDetailPanel
              key={selected.vehicle.id}
              entry={selected}
              onClose={() => selectVehicle(null)}
            />
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
